import { AllProductsDropdownContext } from '@/contexts/AllProductsDropdownContext'
import { MENU_CATEGORY_ORDER } from '@/modules/shared/StoreNavigation/category-order'
import { useMenuCategories } from '@/hooks/use-store-categories'
import { IsMobile } from '@/utils/mobile'
import { isWithCategorie } from '@/utils/store'
import { UnstyledButton } from '@mantine/core'
import Link from 'next/link'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { CategoryNode } from 'stoux-shared-types/services/category'

const stripLeadingNumber = (title: string) => title.replace(/^\d{2}\s/, '')

const QuickCategories = () => {
    const withCategorie = isWithCategorie()
    const { open } = useContext(AllProductsDropdownContext)

    const isMobile = IsMobile()

    // Store front: only the categories where the store has stock
    const { data: categories } = useMenuCategories()

    const [activeCategory, setActiveCategory] = useState<CategoryNode | null>(null)
    const [activeHandle, setActiveHandle] = useState<string | null>(null)
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const quickCategories = useMemo((): CategoryNode[] => {
        if (!categories?.subcategories) return []
        return Object.values(categories.subcategories).sort(sortByHandle)
    }, [categories])

    const subcategories = useMemo(() => {
        if (!activeCategory?.subcategories) return []
        return Object.entries(activeCategory.subcategories)
            .map(([key, node]) => ({ handle: key, node }))
            .sort((a, b) => (a.node.data?.title ?? '').localeCompare(b.node.data?.title ?? ''))
            .slice(0, 10)
    }, [activeCategory])

    const scheduleClose = () => {
        closeTimer.current = setTimeout(() => {
            setActiveCategory(null)
            setActiveHandle(null)
        }, 150)
    }

    const cancelClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current)
    }

    useEffect(() => {
        return () => {
            if (closeTimer.current) clearTimeout(closeTimer.current)
        }
    }, [])

    if (!withCategorie) {
        return (
            <div className="flex items-center justify-center flex-1 w-full px-4 py-0.5 text-sm bg-[var(--primary-dark)] gap-x-8 xl:gap-x-4" />
        )
    }

    return (
        <div className="relative">
            <div className="w-full overflow-x-auto scrollbar-hide bg-[var(--primary-dark)]">
            <div className="flex items-center w-max mx-auto px-4 py-2 text-sm gap-x-6 xl:gap-x-4">
                {quickCategories.map((cat, index) => (
                    <UnstyledButton
                        key={cat.data?.id ?? cat.data?.handle?? `cat-${index}`}
                        className="text-xs text-white capitalize lg:text-sm hover:underline whitespace-nowrap shrink-0"
                        onMouseEnter={() => {
                            if (isMobile) return
                            cancelClose()
                            setActiveCategory(cat)
                            setActiveHandle(cat.data?.handle ?? null)
                        }}
                        onMouseLeave={() => {
                            if (isMobile) return
                            scheduleClose()
                        }}
                        onClick={() => {
                            const handle = cat.data?.handle
                            if (!handle) return
                            if (isMobile) {
                                open(handle)
                            } else {
                                window.location.href = `/search?category=${handle}`
                            }
                        }}
                    >
                        {stripLeadingNumber(cat.data?.title ?? '')}
                    </UnstyledButton>
                ))}
            </div>
            </div>

            {activeCategory && subcategories.length > 0 && (
                <div
                    className="absolute left-0 right-0 z-50 bg-white shadow-xl border-t border-gray-100"
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                >
                    <div className="flex items-center justify-center gap-x-6 px-6 py-3 flex-wrap">
                        {subcategories.map(({ handle, node }) => (
                            <Link
                                key={handle}
                                href={`/search?category=${activeHandle}/${handle}`}
                                className="text-sm text-gray-700 capitalize hover:text-[var(--primary)] hover:underline whitespace-nowrap"
                                onClick={() => {
                                    setActiveCategory(null)
                                    setActiveHandle(null)
                                }}
                            >
                                {stripLeadingNumber(node.data?.title ?? handle)}
                            </Link>
                        ))}
                        <Link
                            href={`/search?category=${activeHandle}`}
                            className="text-sm font-medium text-[var(--primary)] hover:underline whitespace-nowrap"
                            onClick={() => {
                                setActiveCategory(null)
                                setActiveHandle(null)
                            }}
                        >
                            Voir toutes nos catégories →
                        </Link>
                    </div>
                </div>
            )}
        </div>
    )
}

const sortByHandle = (a: CategoryNode, b: CategoryNode) => {
    const ai = MENU_CATEGORY_ORDER.indexOf(a.data?.handle ?? '')
    const bi = MENU_CATEGORY_ORDER.indexOf(b.data?.handle ?? '')
    if (ai === -1 && bi === -1) return 0
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
}

export default QuickCategories
